import $ from 'jquery'
/**
 * @author kostenko
 *
 * Contents of this file:
 *  - SmartField - general purpose smart text field
 *  - DecimalField - decimal number editing field (uses SmartField)
 */

/**
 * Smart field functionality consists of 3 parts:
 * 1. Allows only specific characters to be typed (still, any characters can be pasted). Optionally limits max length.
 * 2. On the field 'change' event (usually when the field loses focus), trims the text and validates it.
 *    Applies the 'invalid' CSS class and adds the 'title' attribute with hint if validation fails.
 * 3. Re-formats the entered text if validation succeeds and removes the 'invalid' class and 'title' attribute
 *    if they were applied before.
 *
 * @param fieldElement HTML input element (can be wrapped into jQuery)
 * @param charFilter function(charCode) - returns boolean whether the given character is allowed in this field
 * @param validator function(text) - returns boolean - whether the given text is valid for this field.
 *        (Empty string is never passed through validator; its correctness is determined by allowEmpty param.)
 * @param formatter function(text) - returns string - the formatted text.
 *        (Empty string is never passed through formatter).
 * @param allowEmpty boolean - whether an empty string is treated as a valid value
 * @param maxLength - maximum number of characters that can be entered. If not specified, then there's no limit.
 * @param [invalidHint] - text to display as tooltip when the field is in 'invalid' state
 * @param [maxValue] - maximum value for this field (value should be < than maxValue)
 */
function SmartField(fieldElement, charFilter, validator, formatter, allowEmpty, maxLength, invalidHint, maxValue)
{
    /**
     * @return string the current value
     */
    this.getValue = function()
    {
        return fieldElement.val();
    };

    /**
     * Sets the new value to the field.
     * Note that this will trigger the 'change' event handler which will trim, validate and re-format the given value.
     * @param value new value to set
     */
    this.setValue = function (value)
    {
        fieldElement.val(value);
        onChange();
    };

    /**
     * Trims, validates and re-formats the current value.
     * This function is invoked automatically on 'change' event, but sometimes it is useful to invoke it manually.
     */
    this.process = function ()
    {
        fieldElement.val(self.getValue().trim());

        if (!self.isValid())
        {
            setInvalidStatus();
        }
        else
        {
            unsetInvalidStatus();

            if (!self.isEmpty())
                fieldElement.val(formatter(self.getValue()));
        }
    };

    /**
     * @return boolean whether the current value is valid
     */
    this.isValid = function()
    {
        return self.isEmpty() ? Boolean(allowEmpty) : validator(self.getValue());
    };

    /**
     * @return boolean whether the current value is an empty string
     */
    this.isEmpty = function()
    {
        return self.getValue().length == 0;
    };

    /**
     * Removes the 'invalid' field status, if set, even if the text is still invalid.
     * This effectively removes the CSS 'invalid' class from the field and removes tooltip.
     */
    this.clearValidationResult = function()
    {
        unsetInvalidStatus();
    };

    this.focus = function()
    {
        fieldElement.focus();
    };

    /**
     * Sets new invalid hint
     * @param newInvalidHint new hint to set
     */
    this.setInvalidHint = function( newInvalidHint )
    {
        invalidHint = newInvalidHint;
        if (!self.isValid())
            setInvalidStatus();
    };

    // ---------- private ----------

    fieldElement = $(fieldElement);
    var self = this;

    function setInvalidStatus()
    {
        fieldElement.addClass('invalid');
        fieldElement.attr('title', invalidHint);
    }

    function unsetInvalidStatus()
    {
        fieldElement.removeClass('invalid');
        fieldElement.removeAttr('title');
    }

    function onKeyPress(event)
    {
        // always allow special keys such as arrows, backspace, etc. (fired under FireFox & Opera only)
        if (event.which < 32)
            return;

        // FireFox, Opera & Safari fire this event even when using modifier keys.
        // In this case we must not prevent the default behavior.
        if (event.ctrlKey || event.altKey || event.metaKey)
            return;

        // apply filter
        if (!charFilter(event.which))
            event.preventDefault();
    }

    function onChange()
    {
        self.process();
    }

    fieldElement.keypress(onKeyPress);
    fieldElement.change(onChange);

    if (maxLength != null)
        fieldElement.attr('maxlength', maxLength);
}

export default SmartField



// WEBPACK FOOTER //
// ./web/js/controls/SmartField.js